import { FormControl, Grid, Input, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import BoxDrawer from 'components/BoxDrawer';
import ButtonConfirmAndClose from 'components/Buttons/ButtonConfirmAndClose';
import { SpinnerIndicator } from 'components/CallServer';
import MuiContainer from 'components/MuiExtras/MuiContainer';
import MuiKeyboardDatePicker from 'components/MuiExtras/MuiKeyboardDatePicker';
import MuiPaper from 'components/MuiExtras/MuiPaper';
import LibUtils from 'lib/LibUtils';
import { getTranslatedUserCredentialsType } from "lib/TranslationUtils";
import { UserCredentialsModel } from 'models/ApiModels';
import { Moment } from 'moment';
import * as React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';

/////////////////////////////////////////////////////////////////////////////////////////////////////////////

interface IState {
    credential: UserCredentialsModel;
    validate: {
        description: boolean;
        date: boolean;
    };
}

interface IProps {
    title: string;
    credential: UserCredentialsModel;
    onClose: (result: UserCredentialsModel) => void;
}

type Props = IProps & WithTranslation;

/////////////////////////////////////////////////////////////////////////////////////////////////////////////


class UserCredentialEditDrawer extends React.Component<Props, IState> {
    constructor(props: Props) {
        super(props);
        this.state = {
            credential: this.props.credential,
            validate: {
                description: !this.props.credential.description ? null : this.props.credential.description.length > 0,
                date: this.checkDateRange(this.props.credential)
            }
        };
    }

    ///
    private checkDateRange(credential: UserCredentialsModel): boolean {
        return credential.startDate == null || credential.endDate == null || credential.startDate <= credential.endDate;
    }

    ///
    private handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        this.updateRecord(
            event.target.name,
            event.target.type == "checkbox"
                ? (event.target as HTMLInputElement).checked
                : (event.target as HTMLInputElement).valueAsNumber
                    ? (event.target as HTMLInputElement).valueAsNumber
                    : event.target.value
        );
    };

    ///
    private updateRecord(name: string, v: any) {
        this.setState(state => {
            switch (name) {
                case "type":
                    state.credential.type = v;
                    break;

                case "description":
                    state.credential.description = v;
                    state.validate.description = v != "";
                    break;

                case "startDate":
                    state.credential.startDate = v;
                    state.validate.date = this.checkDateRange(state.credential);
                    break;

                case "endDate":
                    state.credential.endDate = v;
                    state.validate.date = this.checkDateRange(state.credential);
                    break;
            }
            return state;
        });
    }

    ///
    private renderForm() {
        const { onClose } = this.props;
        const { credential, validate } = this.state;
        const canSave = LibUtils.isAllPropertiesWithValue(this.state.validate, true);
        return (
            <>
                <FormControl fullWidth margin="dense">
                    <InputLabel htmlFor="LabelType">{this.props.t("Common.Literals.TypeLabel")}</InputLabel>
                    <Select
                        name="type"
                        value={credential.type}
                        onChange={(e) => this.handleChange(e as React.ChangeEvent<HTMLInputElement>)}
                        input={<Input id="LabelType" />}
                    >
                        {
                            getTranslatedUserCredentialsType()
                                .map((v) => (
                                    <MenuItem key={v.key} value={v.key}>{v.value}</MenuItem>
                                ))
                        }
                    </Select>
                </FormControl>

                <TextField
                    name="description"
                    label={this.props.t<string>("Common.Literals.DescriptionLabel")}
                    margin="dense"
                    error={validate.description === false}
                    value={credential.description}
                    onChange={this.handleChange}
                    required
                    fullWidth
                    inputProps={{
                        maxLength: 255,
                    }}
                />

                <MuiContainer margin="normal">
                    <Grid container spacing={1}>
                        <Grid item xs={6}>
                            <MuiKeyboardDatePicker
                                fullWidth
                                clearable
                                margin="dense"
                                views={["year", "month"]}
                                label={this.props.t<string>("Common.Literals.DateRangeStartLabel")}
                                error={validate.date === false}
                                value={credential.startDate}
                                onChange={
                                    (date: Moment) => {
                                        if (date == null) {
                                            this.updateRecord("startDate", null);
                                        }
                                        else {
                                            date.set({ hour: 0, minute: 0, second: 0 });
                                            this.updateRecord("startDate", date.toDate());
                                        }
                                    }
                                }
                            />
                        </Grid>

                        <Grid item xs={6}>
                            <MuiKeyboardDatePicker
                                fullWidth
                                clearable
                                margin="dense"
                                views={["year", "month"]}
                                label={this.props.t<string>("Common.Literals.DateRangeEndLabel")}
                                value={credential.endDate}
                                onChange={
                                    (date: Moment) => {
                                        if (date == null) {
                                            this.updateRecord("endDate", null);
                                        }
                                        else {
                                            date.set({ hour: 23, minute: 59, second: 59 });
                                            this.updateRecord("endDate", date.toDate());
                                        }
                                    }
                                }
                            />
                        </Grid>
                    </Grid>
                </MuiContainer>

                <ButtonConfirmAndClose
                    fullWidth
                    disabled={!canSave}
                    onClick={() => { onClose(this.state.credential); }}
                />

            </>
        );
    }


    ///
    public render() {
        const { title, onClose } = this.props;

        return (
            <BoxDrawer
                open
                onClose={() => onClose(null)}
                title={title}
            >
                <MuiPaper dense elevation={0}>
                    {
                        this.state.credential == null
                            ? <SpinnerIndicator />
                            : this.renderForm()
                    }
                </MuiPaper>

            </BoxDrawer>
        );
    }
}

export default withTranslation()(UserCredentialEditDrawer);